import React, { useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
    ArrowUpRight,
    CheckCircle,
    Check,
    Lightning,
    CalendarCheck,
} from "@phosphor-icons/react";
import {
    SECTORS,
    SITE_TYPES,
    MOODS,
    TONES,
    PRIORITIES,
    COPYWRITERS,
    PAGES,
    ASSETS,
    FEATURES,
    PRICE_GROUPS,
    PRICE_INDEX,
    formatCAD,
} from "../data/briefCatalog";
import CalendlyEmbed from "./CalendlyEmbed";
import { getAttribution, pushLeadEvent } from "../lib/utmTracking";
import { trackEvent } from "../lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CALENDLY_URL = process.env.REACT_APP_CALENDLY_URL;

const initialState = {
    full_name: "",
    company: "",
    email: "",
    phone: "",
    sector: "",
    current_website: "",
    site_type: "",
    one_liner: "",
    launch_date: "",
    main_goal: "",
    target_audience: "",
    pages: [],
    pages_other: "",
    mood: "",
    tone: "",
    references: "",
    avoid: "",
    assets: [],
    copywriter: "",
    features: [],
    features_other: "",
    selected_items: [],
    priority: "",
    additional_info: "",
};

export default function Brief() {
    const [form, setForm] = useState(initialState);
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(null);
    const [started, setStarted] = useState(false);

    const markStarted = () => {
        if (!started) {
            setStarted(true);
            trackEvent("brief_start");
        }
    };

    const onChange = (e) => {
        markStarted();
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const toggleArray = (key, value) => {
        markStarted();
        setForm((f) => {
            const arr = f[key] || [];
            return arr.includes(value)
                ? { ...f, [key]: arr.filter((v) => v !== value) }
                : { ...f, [key]: [...arr, value] };
        });
    };

    const togglePriceItem = (item) => {
        markStarted();
        setForm((f) => {
            let next = [...f.selected_items];
            // Handle exclusive groups (e.g. site size)
            if (item.exclusiveGroup) {
                const sameGroupIds = PRICE_GROUPS.flatMap((g) =>
                    g.items
                        .filter((it) => it.exclusiveGroup === item.exclusiveGroup)
                        .map((it) => it.id),
                );
                if (next.includes(item.id)) {
                    next = next.filter((id) => id !== item.id);
                } else {
                    next = next.filter((id) => !sameGroupIds.includes(id));
                    next.push(item.id);
                }
            } else {
                next = next.includes(item.id)
                    ? next.filter((id) => id !== item.id)
                    : [...next, item.id];
            }
            return { ...f, selected_items: next };
        });
    };

    const total = useMemo(() => {
        return form.selected_items.reduce((sum, id) => {
            const it = PRICE_INDEX[id];
            return it ? sum + it.price : sum;
        }, 0);
    }, [form.selected_items]);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.full_name.trim() || !form.company.trim() || !form.email.trim()) {
            toast.error("Nom, entreprise et courriel sont requis.");
            return;
        }
        setLoading(true);
        try {
            const attribution = getAttribution();
            const { data } = await axios.post(`${API}/brief`, {
                ...form,
                ...attribution,
                estimated_total: total,
            });
            setDone(data);
            // Push GA4 / GTM lead event for attribution analytics
            pushLeadEvent({
                reference: data.reference,
                value: data.estimated_total || total,
                currency: "CAD",
            });
            // Homegrown analytics event (funnel: brief_submit)
            trackEvent("brief_submit", {
                reference: data.reference,
                value: data.estimated_total || total,
            });
            const target = document.getElementById("contact");
            if (target) {
                window.scrollTo({ top: target.offsetTop - 60, behavior: "smooth" });
            }
            toast.success("Brief envoyé ! Nous revenons vers vous sous 24-48 h.");
        } catch (err) {
            console.error(err);
            toast.error(
                err?.response?.data?.detail ||
                    "Erreur lors de l'envoi. Vérifiez vos informations.",
            );
        } finally {
            setLoading(false);
        }
    };

    if (done) {
        return (
            <section
                id="contact"
                data-testid="brief-success-section"
                className="relative py-24 md:py-32 px-6 md:px-12 lg:px-16 border-t border-scd"
            >
                <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.5 }}
                    className="max-w-3xl mx-auto border border-scd bg-elev rounded-sm p-10 md:p-14 text-center"
                >
                    <CheckCircle
                        size={64}
                        weight="duotone"
                        className="text-yellow mx-auto"
                    />
                    <span className="label-eyebrow text-yellow mt-6 block">
                        Référence : {done.reference}
                    </span>
                    <h3 className="font-display text-4xl md:text-5xl mt-4 text-cream">
                        Brief reçu, merci&nbsp;!
                    </h3>
                    <p className="mt-5 text-cream/75 text-lg max-w-xl mx-auto">
                        Notre équipe analyse votre demande dès maintenant. Vous
                        recevrez une soumission détaillée dans les prochaines{" "}
                        <span className="text-yellow">24 à 48 heures</span>.
                    </p>
                    {done.estimated_total > 0 && (
                        <div className="mt-10 inline-flex flex-col items-center gap-1 px-8 py-6 border border-scd rounded-sm bg-scd">
                            <span className="label-eyebrow text-cream/60">
                                Estimation indicative
                            </span>
                            <span className="font-display text-4xl text-yellow">
                                {formatCAD(done.estimated_total)}
                            </span>
                            <span className="text-xs text-cream/50">
                                Avant taxes (TPS + TVQ) — soumission formelle à venir
                            </span>
                        </div>
                    )}
                </motion.div>

                {/* Calendly booking */}
                {CALENDLY_URL && (
                    <motion.div
                        initial={{ y: 30, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ duration: 0.6, delay: 0.3 }}
                        className="max-w-5xl mx-auto mt-16"
                        data-testid="brief-calendly-section"
                    >
                        <div className="text-center mb-8">
                            <CalendarCheck
                                size={36}
                                weight="duotone"
                                className="text-yellow mx-auto"
                            />
                            <span className="label-eyebrow text-yellow mt-4 block">
                                — Étape suivante
                            </span>
                            <h4 className="font-display text-3xl md:text-4xl mt-3 text-cream">
                                Réservez votre{" "}
                                <span className="text-yellow italic">
                                    appel découverte
                                </span>{" "}
                                gratuit.
                            </h4>
                            <p className="text-cream/70 mt-3 max-w-xl mx-auto">
                                30 minutes pour discuter de votre projet, valider
                                l'estimation et planifier les prochaines étapes.
                            </p>
                        </div>
                        <div className="border border-scd bg-elev rounded-sm overflow-hidden">
                            <CalendlyEmbed
                                url={CALENDLY_URL}
                                height={780}
                                prefill={{
                                    name: done.full_name,
                                    email: done.email,
                                    customAnswers: {
                                        a1: `Brief ${done.reference} — ${done.company}`,
                                    },
                                }}
                            />
                        </div>
                    </motion.div>
                )}

                <div className="mt-12 text-center">
                    <button
                        onClick={() => {
                            setDone(null);
                            setForm(initialState);
                        }}
                        data-testid="brief-new-button"
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-scd text-cream hover:border-yellow hover:text-yellow transition-colors"
                    >
                        Soumettre un nouveau brief
                    </button>
                </div>
            </section>
        );
    }

    return (
        <section
            id="contact"
            data-testid="brief-section"
            className="relative py-24 md:py-32 px-6 md:px-12 lg:px-16 border-t border-scd"
        >
            {/* Header */}
            <div className="grid md:grid-cols-12 gap-8 mb-14 md:mb-20">
                <div className="md:col-span-4">
                    <span className="label-eyebrow text-yellow">— Brief client</span>
                </div>
                <div className="md:col-span-8">
                    <h2 className="section-heading text-cream" data-testid="brief-heading">
                        Démarrons
                        <br />
                        <span className="text-yellow italic">votre projet.</span>
                    </h2>
                    <p className="mt-6 max-w-2xl text-cream/75 text-lg">
                        Remplissez le brief ci-dessous — l'estimation se calcule en
                        temps réel. Une soumission formelle vous sera envoyée sous
                        24-48&nbsp;h après analyse.
                    </p>
                </div>
            </div>

            <form onSubmit={onSubmit} data-testid="brief-form" className="grid lg:grid-cols-12 gap-10">
                {/* Form column */}
                <div className="lg:col-span-8 space-y-16">
                    {/* 01 - Informations client */}
                    <Section number="01" title="Informations client">
                        <div className="grid md:grid-cols-2 gap-5">
                            <Field label="Nom complet *">
                                <input
                                    name="full_name"
                                    value={form.full_name}
                                    onChange={onChange}
                                    required
                                    data-testid="brief-input-full-name"
                                    className="scd-input"
                                    placeholder="Jean Tremblay"
                                />
                            </Field>
                            <Field label="Nom de l'entreprise *">
                                <input
                                    name="company"
                                    value={form.company}
                                    onChange={onChange}
                                    required
                                    data-testid="brief-input-company"
                                    className="scd-input"
                                    placeholder="Café Montréal Inc."
                                />
                            </Field>
                            <Field label="Courriel *">
                                <input
                                    name="email"
                                    type="email"
                                    value={form.email}
                                    onChange={onChange}
                                    required
                                    data-testid="brief-input-email"
                                    className="scd-input"
                                    placeholder="vous@entreprise.com"
                                />
                            </Field>
                            <Field label="Téléphone">
                                <input
                                    name="phone"
                                    value={form.phone}
                                    onChange={onChange}
                                    data-testid="brief-input-phone"
                                    className="scd-input"
                                    placeholder="514 000-0000"
                                />
                            </Field>
                            <Field label="Secteur d'activité">
                                <select
                                    name="sector"
                                    value={form.sector}
                                    onChange={onChange}
                                    data-testid="brief-select-sector"
                                    className="scd-input"
                                >
                                    {SECTORS.map((s) => (
                                        <option
                                            key={s}
                                            value={s === "Choisir" ? "" : s}
                                        >
                                            {s}
                                        </option>
                                    ))}
                                </select>
                            </Field>
                            <Field label="Site web actuel (si applicable)">
                                <input
                                    name="current_website"
                                    value={form.current_website}
                                    onChange={onChange}
                                    data-testid="brief-input-current-website"
                                    className="scd-input"
                                    placeholder="https://..."
                                />
                            </Field>
                        </div>
                    </Section>

                    {/* 02 - Le projet */}
                    <Section number="02" title="Le projet">
                        <div className="grid md:grid-cols-2 gap-5">
                            <Field label="Type de site">
                                <select
                                    name="site_type"
                                    value={form.site_type}
                                    onChange={onChange}
                                    data-testid="brief-select-site-type"
                                    className="scd-input"
                                >
                                    {SITE_TYPES.map((s) => (
                                        <option
                                            key={s}
                                            value={s === "Choisir" ? "" : s}
                                        >
                                            {s}
                                        </option>
                                    ))}
                                </select>
                            </Field>
                            <Field label="Date de lancement souhaitée">
                                <input
                                    name="launch_date"
                                    type="date"
                                    value={form.launch_date}
                                    onChange={onChange}
                                    data-testid="brief-input-launch-date"
                                    className="scd-input"
                                />
                            </Field>
                        </div>
                        <Field label="Description en une phrase">
                            <input
                                name="one_liner"
                                value={form.one_liner}
                                onChange={onChange}
                                data-testid="brief-input-one-liner"
                                className="scd-input"
                                placeholder="Café de spécialité offrant des cours de torréfaction à Montréal."
                            />
                        </Field>
                        <Field label="Objectif principal du site">
                            <textarea
                                name="main_goal"
                                rows={3}
                                value={form.main_goal}
                                onChange={onChange}
                                data-testid="brief-input-main-goal"
                                className="scd-input resize-none"
                                placeholder="Ex.: Générer des réservations pour nos cours, présenter notre menu, vendre nos produits en ligne..."
                            />
                        </Field>
                        <Field label="Public cible">
                            <textarea
                                name="target_audience"
                                rows={2}
                                value={form.target_audience}
                                onChange={onChange}
                                data-testid="brief-input-target-audience"
                                className="scd-input resize-none"
                                placeholder="Ex.: Professionnels 30-45 ans, Montréal, amateurs de café de spécialité."
                            />
                        </Field>
                    </Section>

                    {/* 03 - Pages souhaitées */}
                    <Section number="03" title="Pages souhaitées">
                        <p className="text-cream/65 text-sm mb-5">
                            Cochez toutes les pages à inclure dans le site.
                        </p>
                        <div className="grid sm:grid-cols-2 gap-3">
                            {PAGES.map((p) => (
                                <Pill
                                    key={p.id}
                                    active={form.pages.includes(p.id)}
                                    onClick={() => toggleArray("pages", p.id)}
                                    testId={`brief-page-${p.id}`}
                                >
                                    {p.label}
                                </Pill>
                            ))}
                        </div>
                        <Field label="Pages supplémentaires (autres)" className="mt-5">
                            <input
                                name="pages_other"
                                value={form.pages_other}
                                onChange={onChange}
                                data-testid="brief-input-pages-other"
                                className="scd-input"
                                placeholder="Ex.: Espace membre, livraison, politique de retour..."
                            />
                        </Field>
                    </Section>

                    {/* 04 - Style et identité visuelle */}
                    <Section number="04" title="Style et identité visuelle">
                        <div className="grid md:grid-cols-2 gap-5">
                            <Field label="Ambiance souhaitée">
                                <select
                                    name="mood"
                                    value={form.mood}
                                    onChange={onChange}
                                    data-testid="brief-select-mood"
                                    className="scd-input"
                                >
                                    {MOODS.map((s) => (
                                        <option
                                            key={s}
                                            value={s === "Choisir" ? "" : s}
                                        >
                                            {s}
                                        </option>
                                    ))}
                                </select>
                            </Field>
                            <Field label="Ton de la communication">
                                <select
                                    name="tone"
                                    value={form.tone}
                                    onChange={onChange}
                                    data-testid="brief-select-tone"
                                    className="scd-input"
                                >
                                    {TONES.map((s) => (
                                        <option
                                            key={s}
                                            value={s === "Choisir" ? "" : s}
                                        >
                                            {s}
                                        </option>
                                    ))}
                                </select>
                            </Field>
                        </div>
                        <Field label="Sites de référence (URLs que vous aimez)">
                            <textarea
                                name="references"
                                rows={2}
                                value={form.references}
                                onChange={onChange}
                                data-testid="brief-input-references"
                                className="scd-input resize-none"
                                placeholder="https://..., https://..., https://..."
                            />
                        </Field>
                        <Field label="À éviter (styles, couleurs, éléments)">
                            <textarea
                                name="avoid"
                                rows={2}
                                value={form.avoid}
                                onChange={onChange}
                                data-testid="brief-input-avoid"
                                className="scd-input resize-none"
                                placeholder="Ex.: Pas de couleurs trop vives. Éviter les photos de banque d'images."
                            />
                        </Field>
                    </Section>

                    {/* 05 - Contenu et assets */}
                    <Section number="05" title="Contenu et assets">
                        <p className="text-cream/65 text-sm mb-5">
                            Cochez les éléments que vous nous fournirez.
                        </p>
                        <div className="grid sm:grid-cols-2 gap-3">
                            {ASSETS.map((a) => (
                                <Pill
                                    key={a.id}
                                    active={form.assets.includes(a.id)}
                                    onClick={() => toggleArray("assets", a.id)}
                                    testId={`brief-asset-${a.id}`}
                                >
                                    {a.label}
                                </Pill>
                            ))}
                        </div>
                        <Field label="Qui rédige les textes ?" className="mt-5">
                            <select
                                name="copywriter"
                                value={form.copywriter}
                                onChange={onChange}
                                data-testid="brief-select-copywriter"
                                className="scd-input"
                            >
                                {COPYWRITERS.map((s) => (
                                    <option
                                        key={s}
                                        value={s === "Choisir" ? "" : s}
                                    >
                                        {s}
                                    </option>
                                ))}
                            </select>
                        </Field>
                    </Section>

                    {/* 06 - Fonctionnalités */}
                    <Section number="06" title="Fonctionnalités requises">
                        <p className="text-cream/65 text-sm mb-5">
                            Sélectionnez toutes les fonctionnalités souhaitées.
                        </p>
                        <div className="grid sm:grid-cols-2 gap-3">
                            {FEATURES.map((f) => (
                                <Pill
                                    key={f.id}
                                    active={form.features.includes(f.id)}
                                    onClick={() => toggleArray("features", f.id)}
                                    testId={`brief-feature-${f.id}`}
                                >
                                    {f.label}
                                </Pill>
                            ))}
                        </div>
                        <Field label="Autres fonctionnalités (préciser)" className="mt-5">
                            <input
                                name="features_other"
                                value={form.features_other}
                                onChange={onChange}
                                data-testid="brief-input-features-other"
                                className="scd-input"
                                placeholder="Ex.: Calculateur de prix, espace membres, intégration Calendly..."
                            />
                        </Field>
                    </Section>

                    {/* 07 - Estimation */}
                    <Section number="07" title="Estimation du projet">
                        <p className="text-cream/65 text-sm mb-8">
                            Sélectionnez les éléments souhaités — le total se calcule
                            automatiquement. Cette estimation est indicative; une
                            soumission formelle sera produite après analyse du brief.
                        </p>
                        <div className="space-y-10">
                            {PRICE_GROUPS.map((g) => (
                                <div key={g.id}>
                                    <h4 className="label-eyebrow text-yellow mb-4">
                                        {g.title}
                                    </h4>
                                    <div className="grid md:grid-cols-2 gap-3">
                                        {g.items.map((it) => {
                                            const active = form.selected_items.includes(
                                                it.id,
                                            );
                                            return (
                                                <button
                                                    key={it.id}
                                                    type="button"
                                                    onClick={() => togglePriceItem(it)}
                                                    data-testid={`brief-price-${it.id}`}
                                                    className={`text-left rounded-sm border p-4 transition-all ${
                                                        active
                                                            ? "border-green bg-[var(--scd-yellow-tint)]"
                                                            : "border-scd bg-elev hover:border-green/60"
                                                    }`}
                                                >
                                                    <div className="flex items-start justify-between gap-4">
                                                        <div>
                                                            <div className="font-display text-base text-cream leading-tight">
                                                                {it.label}
                                                            </div>
                                                            <div className="text-cream/60 text-xs mt-1.5">
                                                                {it.desc}
                                                            </div>
                                                        </div>
                                                        <div
                                                            className={`mt-1 shrink-0 w-5 h-5 rounded-sm border-2 flex items-center justify-center ${
                                                                active
                                                                    ? "bg-green border-green"
                                                                    : "border-cream/30"
                                                            }`}
                                                        >
                                                            {active && (
                                                                <Check
                                                                    size={12}
                                                                    weight="bold"
                                                                    className="text-[#faf7f1]"
                                                                />
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="mt-3 font-display text-yellow">
                                                        {formatCAD(it.price)}
                                                        {it.suffix && (
                                                            <span className="text-cream/60 text-xs ml-1">
                                                                {it.suffix}
                                                            </span>
                                                        )}
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Section>

                    {/* 08 - Final */}
                    <Section number="08" title="Priorité & informations complémentaires">
                        <Field label="Niveau de priorité">
                            <select
                                name="priority"
                                value={form.priority}
                                onChange={onChange}
                                data-testid="brief-select-priority"
                                className="scd-input"
                            >
                                {PRIORITIES.map((s) => (
                                    <option
                                        key={s}
                                        value={s === "Choisir" ? "" : s}
                                    >
                                        {s}
                                    </option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Informations complémentaires">
                            <textarea
                                name="additional_info"
                                rows={4}
                                value={form.additional_info}
                                onChange={onChange}
                                data-testid="brief-input-additional"
                                className="scd-input resize-none"
                                placeholder="Tout ce qui n'a pas été couvert ci-dessus : contraintes techniques, intégrations existantes, contexte particulier, questions..."
                            />
                        </Field>

                        <div className="pt-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <span className="text-xs text-cream/55">
                                Vos informations sont confidentielles et utilisées
                                uniquement dans le cadre de votre projet.
                            </span>
                            <button
                                type="submit"
                                disabled={loading}
                                data-testid="brief-submit-button"
                                className="group inline-flex items-center gap-3 px-7 py-4 rounded-full bg-yellow text-[#272727] font-semibold text-base hover:bg-green hover:text-[#faf7f1] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {loading ? "Envoi..." : "Soumettre le brief"}
                                <ArrowUpRight
                                    size={18}
                                    weight="bold"
                                    className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
                                />
                            </button>
                        </div>
                    </Section>
                </div>

                {/* Sticky price summary */}
                <aside className="lg:col-span-4">
                    <div
                        className="lg:sticky lg:top-28 border border-scd bg-elev rounded-sm p-6 md:p-7"
                        data-testid="brief-price-summary"
                    >
                        <div className="flex items-center gap-2">
                            <Lightning size={18} weight="bold" className="text-yellow" />
                            <span className="label-eyebrow text-yellow">
                                Estimation en temps réel
                            </span>
                        </div>
                        <div className="mt-5 font-display text-5xl md:text-6xl text-cream leading-none">
                            <span data-testid="brief-total-amount">
                                {formatCAD(total)}
                            </span>
                        </div>
                        <p className="text-xs text-cream/55 mt-3">
                            Estimation indicative avant taxes (TPS + TVQ).
                            Dollars canadiens. Une soumission formelle sera produite
                            suite à l'analyse de votre brief.
                        </p>

                        {form.selected_items.length > 0 && (
                            <div className="mt-6 pt-6 border-t border-scd space-y-2.5 max-h-[40vh] overflow-y-auto">
                                {form.selected_items.map((id) => {
                                    const it = PRICE_INDEX[id];
                                    if (!it) return null;
                                    return (
                                        <div
                                            key={id}
                                            className="flex items-start justify-between gap-3 text-sm"
                                        >
                                            <span className="text-cream/85 leading-snug">
                                                {it.label}
                                            </span>
                                            <span className="text-yellow font-medium whitespace-nowrap">
                                                {formatCAD(it.price)}
                                                {it.suffix && (
                                                    <span className="text-cream/50 text-xs">
                                                        {it.suffix}
                                                    </span>
                                                )}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </aside>
            </form>

            <style>{`
                .scd-input {
                    width: 100%;
                    background: #faf7f1;
                    color: #272727;
                    border: 1px solid rgba(39,39,39,0.18);
                    padding: 0.85rem 1rem;
                    border-radius: 4px;
                    font-family: "Manrope", sans-serif;
                    font-size: 0.95rem;
                    transition: border-color 0.25s ease, box-shadow 0.25s ease;
                }
                .scd-input:focus {
                    outline: none;
                    border-color: #3b5838;
                    box-shadow: 0 0 0 3px rgba(59,88,56,0.18);
                }
                .scd-input::placeholder { color: rgba(39,39,39,0.4); }
                select.scd-input { appearance: none; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='none' stroke='%23272727' stroke-width='1.5' d='M1 1l5 5 5-5'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 1rem center; padding-right: 2.5rem; }
            `}</style>
        </section>
    );
}

function Section({ number, title, children }) {
    return (
        <motion.div
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.6 }}
            data-testid={`brief-section-${number}`}
            className="scroll-mt-24"
        >
            <div className="flex items-baseline gap-5 mb-6 pb-4 border-b border-scd">
                <span className="font-display text-2xl text-yellow">{number}</span>
                <h3 className="font-display text-2xl md:text-3xl text-cream">
                    {title}
                </h3>
            </div>
            <div className="space-y-5">{children}</div>
        </motion.div>
    );
}

function Field({ label, children, className = "" }) {
    return (
        <label className={`block ${className}`}>
            <span className="label-eyebrow text-cream/70 mb-2 block">{label}</span>
            {children}
        </label>
    );
}

function Pill({ active, onClick, children, testId }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            className={`text-left rounded-sm border px-4 py-3 transition-all flex items-center gap-3 ${
                active
                    ? "border-green bg-[var(--scd-yellow-tint)] text-cream"
                    : "border-scd bg-elev text-cream/85 hover:border-green/60"
            }`}
        >
            <span
                className={`shrink-0 w-5 h-5 rounded-sm border-2 flex items-center justify-center ${
                    active ? "bg-green border-green" : "border-cream/30"
                }`}
            >
                {active && (
                    <Check size={12} weight="bold" className="text-[#faf7f1]" />
                )}
            </span>
            <span className="text-sm">{children}</span>
        </button>
    );
}

function AssetPill() {
    return null; // deprecated — kept as no-op placeholder
}

